import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@payroll/utils/api'
import { formatCurrency, formatPeriod, formatMonthEn } from '@payroll/utils/format'
import type { DashboardStats, PayrollPeriod } from '@payroll/types'
import { Button } from '@payroll/components/ui/button'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@payroll/components/ui/card'
import { Badge } from '@payroll/components/ui/badge'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@payroll/components/ui/table'
import {
  Users,
  DollarSign,
  Receipt,
  Shield,
  Play,
  UserPlus,
  FileBarChart,
  Loader2,
  AlertCircle,
  CalendarDays,
} from 'lucide-react'

// ── Helpers ─────────────────────────────────────────────────────────────────

const now = new Date()
const currentYear = now.getFullYear()
const currentMonth = now.getMonth() + 1

const STATUS_STEPS = ['draft', 'finalized', 'paid', 'emailed'] as const

function statusStepIndex(status: string): number {
  const idx = STATUS_STEPS.indexOf(status as (typeof STATUS_STEPS)[number])
  return idx === -1 ? 0 : idx
}

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    draft: 'Draft',
    finalized: 'Finalized',
    paid: 'Paid',
    emailed: 'Emailed',
  }
  return map[status] || status
}

function statusBadgeVariant(
  status: string
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'emailed':
      return 'default'
    case 'paid':
      return 'default'
    case 'finalized':
      return 'secondary'
    default:
      return 'outline'
  }
}

// ── Component ───────────────────────────────────────────────────────────────

export default function DashboardPage() {
  // Fetch current month summary
  const {
    data: statsResponse,
    isLoading: statsLoading,
  } = useQuery({
    queryKey: ['dashboard-stats', currentYear, currentMonth],
    queryFn: async () => {
      try {
        return await api.get<DashboardStats>(`/reports/summary/${currentYear}/${currentMonth}`)
      } catch {
        // No payroll period yet — return empty stats
        return { success: true, data: null as unknown as DashboardStats }
      }
    },
  })

  // Fetch payroll periods
  const {
    data: periodsResponse,
    isLoading: periodsLoading,
    error: periodsError,
  } = useQuery({
    queryKey: ['payroll-periods'],
    queryFn: () => api.get<PayrollPeriod[]>('/payroll/periods'),
  })

  const stats = statsResponse?.data
  const periods = periodsResponse?.data

  // Current period (matching current year/month or latest)
  const currentPeriod = useMemo(() => {
    if (!periods || periods.length === 0) return null
    const match = periods.find(
      (p) => p.year === currentYear && p.month === currentMonth
    )
    return match || periods[0]
  }, [periods])

  // Recent periods (last 6)
  const recentPeriods = useMemo(() => {
    if (!periods) return []
    const sorted = [...periods].sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year
      return b.month - a.month
    })
    return sorted.slice(0, 6)
  }, [periods])

  const isLoading = statsLoading || periodsLoading
  const hasError = periodsError

  // ── Stat Cards data ───────────────────────────────────────────────
  const statCards = [
    {
      title: 'Active Employees',
      value: stats?.active_employees?.toString() ?? '-',
      icon: Users,
      color: 'bg-blue-50 text-blue-700',
    },
    {
      title: 'Monthly Payroll',
      value: stats ? formatCurrency(stats.total_payroll) : '-',
      icon: DollarSign,
      color: 'bg-green-50 text-green-700',
    },
    {
      title: 'Tax Withheld',
      value: stats ? formatCurrency(stats.total_tax) : '-',
      icon: Receipt,
      color: 'bg-amber-50 text-amber-700',
    },
    {
      title: 'Social Security',
      value: stats ? formatCurrency(stats.total_ss) : '-',
      icon: Shield,
      color: 'bg-purple-50 text-purple-700',
    },
  ]

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-primary-900">
          Dashboard
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {formatPeriod(currentYear, currentMonth)}
        </p>
      </div>

      {/* Error banner */}
      {hasError && (
        <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            Failed to load data. Please try refreshing the page.
          </span>
        </div>
      )}

      {/* Loading overlay for stat cards */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary-900 mr-2" />
          <span className="text-sm text-gray-500">Loading...</span>
        </div>
      )}

      {/* ── Stat Cards ────────────────────────────────────────────── */}
      {!isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((card) => {
            const Icon = card.icon
            return (
              <Card key={card.title}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-gray-500">
                        {card.title}
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {card.value}
                      </p>
                    </div>
                    <div
                      className={`flex items-center justify-center w-12 h-12 rounded-lg ${card.color}`}
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* ── Current Period Status ──────────────────────────────────── */}
      {!isLoading && currentPeriod && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg text-primary-900">
                  Current Period Status
                </CardTitle>
                <CardDescription>
                  {formatPeriod(currentPeriod.year, currentPeriod.month)}
                </CardDescription>
              </div>
              <Badge variant={statusBadgeVariant(currentPeriod.status)}>
                {statusLabel(currentPeriod.status)}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {/* Progress bar */}
            <div className="space-y-3">
              <div className="flex justify-between">
                {STATUS_STEPS.map((step, index) => {
                  const active =
                    index <= statusStepIndex(currentPeriod.status)
                  return (
                    <div key={step} className="flex flex-col items-center flex-1">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                          active
                            ? 'bg-primary-900 text-white'
                            : 'bg-gray-200 text-gray-500'
                        }`}
                      >
                        {index + 1}
                      </div>
                      <p
                        className={`text-xs mt-1 text-center ${
                          active
                            ? 'text-primary-900 font-medium'
                            : 'text-gray-400'
                        }`}
                      >
                        {statusLabel(step)}
                      </p>
                    </div>
                  )
                })}
              </div>

              {/* Connector bar */}
              <div className="relative mx-4">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 rounded-full" />
                <div
                  className="absolute top-0 left-0 h-1 bg-primary-900 rounded-full transition-all duration-500"
                  style={{
                    width: `${(statusStepIndex(currentPeriod.status) / (STATUS_STEPS.length - 1)) * 100}%`,
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Quick Actions ─────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg text-primary-900">
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button asChild>
              <Link to="/payroll">
                <Play className="w-4 h-4 mr-2" />
                Run Payroll
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/employees">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Employee
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/reports">
                <FileBarChart className="w-4 h-4 mr-2" />
                View Reports
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Recent Payroll Periods ────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-primary-900" />
            <div>
              <CardTitle className="text-lg text-primary-900">
                Recent Payroll Periods
              </CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {periodsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
              <span className="text-sm text-gray-500">Loading...</span>
            </div>
          ) : recentPeriods.length === 0 ? (
            <div className="text-center py-8">
              <CalendarDays className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">
                No payroll periods yet
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    Month
                  </TableHead>
                  <TableHead>
                    Status
                  </TableHead>
                  <TableHead className="text-right">
                    Total Payroll
                  </TableHead>
                  <TableHead className="text-right">
                    Total Tax
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentPeriods.map((period) => (
                  <TableRow key={period.id}>
                    <TableCell>
                      <Link
                        to={`/payroll/${period.id}`}
                        className="text-primary-900 hover:underline font-medium"
                      >
                        {formatMonthEn(period.month)} {period.year}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant(period.status)}>
                        {statusLabel(period.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {/* Total payroll will be computed server-side; use placeholder if not available */}
                      -
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      -
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
